import axios from 'axios';

// Get API URL from environment or default based on environment
// This works for both development and production without code changes
const getApiBaseUrl = () => {
  // 1. Check for REACT_APP_API_URL (standard, can be full URL like http://localhost:8000/api)
  if (process.env.REACT_APP_API_URL) {
    const url = process.env.REACT_APP_API_URL.trim();
    // If it already ends with /api, use it as is
    if (url.endsWith('/api')) {
      return url;
    }
    // If it's a base URL without /api, add it
    if (url.startsWith('http')) {
      return `${url}/api`;
    }
    // Otherwise use as is (for relative paths)
    return url;
  }
  
  // 2. Check for REACT_APP_API_BASE_URL (alternative, usually base URL without /api)
  if (process.env.REACT_APP_API_BASE_URL) {
    const baseUrl = process.env.REACT_APP_API_BASE_URL.trim();
    return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  }
  
  // 3. Auto-detect based on environment
  // In development (when running npm start), use localhost
  // In production (when built), use relative path
  if (process.env.NODE_ENV === 'development') {
    // Check if we're running in browser (not SSR)
    if (typeof window !== 'undefined') {
      // If accessing via localhost, use localhost backend
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:8000/api';
      }
    }
  }
  
  // 4. Production fallback - relative path (works with nginx proxy)
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Don't warn for auth endpoints - they don't need tokens
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isLoginEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');
      const isOnLoginPage = window.location.pathname === '/login' || window.location.pathname.includes('/login');
      
      // Don't redirect if:
      // 1. It's a login/register request (let the login page handle the error)
      // 2. We're already on the login page
      if (!isLoginEndpoint && !isOnLoginPage) {
        // Unauthorized - clear token and redirect to login
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to build query params
const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === null || value === undefined || value === '') {
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== null && item !== undefined && item !== '') {
          queryParams.append(key, item);
        }
      });
      return;
    }
    queryParams.append(key, value);
  });
  return queryParams.toString();
};

// API Client object with all endpoints
const APIClient = {
  // Auth endpoints
  auth: {
    login: async (credentials) => {
      const formData = new URLSearchParams();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);
      return api.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
    },
  },

  // Clients endpoints
  clients: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/clients/?${queryString}`);
    },
    getById: (id) => api.get(`/clients/${id}`),
    create: (data) => api.post('/clients/', data),
    update: (id, data) => api.put(`/clients/${id}`, data),
    delete: (id) => api.delete(`/clients/${id}`),
    hardDelete: (id) => api.delete(`/clients/${id}/hard`),
    getStatistics: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/clients/summary/statistics?${queryString}`);
    },
    getPublicToken: (clientId) => api.get(`/clients/${clientId}/public-token`),
    // Billing Info endpoints
    getBillingInfo: (clientId) => api.get(`/clients/${clientId}/billing-info`),
    createBillingInfo: (clientId, data) => api.post(`/clients/${clientId}/billing-info`, data),
    updateBillingInfo: (billingInfoId, data) => api.put(`/clients/billing-info/${billingInfoId}`, data),
    deleteBillingInfo: (billingInfoId) => api.delete(`/clients/billing-info/${billingInfoId}`),
  },

  // Projects endpoints
  projects: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/projects/?${queryString}`);
    },
    getById: (id) => api.get(`/projects/${id}`),
    create: (data) => api.post('/projects/', data),
    update: (id, data) => api.put(`/projects/${id}`, data),
    delete: (id) => api.delete(`/projects/${id}`),
  },

  // Service Templates endpoints
  serviceTemplates: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/service-templates/?${queryString}`);
    },
    getById: (id) => api.get(`/service-templates/${id}`),
    create: (data) => api.post('/service-templates/', data),
    update: (id, data) => api.put(`/service-templates/${id}`, data),
    delete: (id) => api.delete(`/service-templates/${id}`),
  },

  // Services endpoints
  services: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/services/?${queryString}`);
    },
    getById: (id) => api.get(`/services/${id}`),
    create: (data) => api.post('/services/', data),
    update: (id, data) => api.put(`/services/${id}`, data),
    delete: (id) => api.delete(`/services/${id}`),
    getPaymentSummary: (id) => api.get(`/services/${id}/payment-summary`),
    getClientServicesPaymentSummary: (clientId) => {
      return api.get(`/services/client/${clientId}/payment-summary`);
    },
    getStatistics: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/services/summary/statistics?${queryString}`);
    },
    // Public endpoints (no auth required - use axios directly, not api instance)
    getPublicServices: (token, params = {}) => {
      const queryString = buildQueryParams(params);
      // Use axios directly to avoid auth interceptor
      return axios.get(`${API_BASE_URL}/services/public/${token}?${queryString}`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
    },
    getPublicStatistics: (token) => {
      // Use axios directly to avoid auth interceptor
      return axios.get(`${API_BASE_URL}/services/public/${token}/statistics`, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
    },
  },

  // Payments endpoints
  payments: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/payments/?${queryString}`);
    },
    getById: (id) => api.get(`/payments/${id}`),
    create: (data) => api.post('/payments/', data),
    update: (id, data) => api.put(`/payments/${id}`, data),
    delete: (id) => api.delete(`/payments/${id}`),
    getByClient: (clientId, params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/payments/client/${clientId}?${queryString}`);
    },
    getClientSummary: (clientId) => api.get(`/payments/client/${clientId}/summary`),
    getByInvoice: (invoiceId, params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/payments/invoice/${invoiceId}?${queryString}`);
    },
    getInvoiceSummary: (invoiceId) => api.get(`/payments/invoice/${invoiceId}/summary`),
    getStatistics: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/payments/statistics?${queryString}`);
    },
    suggestAllocations: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/payments/allocation-suggestions?${queryString}`);
    },
    downloadReceipt: (id, config = {}) => api.get(`/payments/${id}/receipt`, { responseType: 'blob', ...config }),
  },

  licences: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/licences/?${queryString}`);
    },
    getById: (id) => api.get(`/licences/${id}`),
    create: (data) => api.post('/licences/', data),
    update: (id, data) => api.put(`/licences/${id}`, data),
    updateStatus: (id, status) => api.patch(`/licences/${id}/status`, { status }),
    getProducts: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/licences/products?${queryString}`);
    },
    createProduct: (data) => api.post('/licences/products', data),
    updateProduct: (id, data) => api.put(`/licences/products/${id}`, data),
    deleteProduct: (id) => api.delete(`/licences/products/${id}`),
    unblockDomain: (id, domain) => api.patch(`/licences/${id}/unblock`, { domain }),
    getStatistics: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/licences/statistics?${queryString}`);
    },
  },

  // Payment Allocations: μόνο ανάγνωση· δημιουργία/επεξεργασία μέσω POST/PUT /payments
  paymentAllocations: {
    listByPayment: (paymentId) => api.get(`/payment-allocations/payment/${paymentId}`),
    listByPaymentsBatch: (paymentIds) => {
      const idsString = Array.isArray(paymentIds) ? paymentIds.join(',') : paymentIds;
      return api.get(`/payment-allocations/payments/batch?payment_ids=${idsString}`);
    },
  },

  // Invoices endpoints
  invoices: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/invoices/?${queryString}`);
    },
    getById: (id) => api.get(`/invoices/${id}`),
    getDeleteInfo: (id) => api.get(`/invoices/${id}/delete-info`),
    create: (data) => api.post('/invoices/', data),
    update: (id, data) => api.put(`/invoices/${id}`, data),
    delete: (id) => api.delete(`/invoices/${id}`),
    updatePayment: (id, data) => api.patch(`/invoices/${id}/payment`, data),
    createFromServices: (params, serviceIds) => {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      serviceIds.forEach(id => queryParams.append('service_ids', id.toString()));
      return api.post(`/invoices/create-from-services?${queryParams}`);
    },
    updateGreekCalculations: (id, params) => {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      return api.patch(`/invoices/${id}/greek-calculations?${queryParams}`);
    },
    uploadToMyData: (id, params) => {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key].toString());
        }
      });
      return api.post(`/invoices/${id}/mydata/upload?${queryParams}`);
    },
    cancelMyData: (id, params) => {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
          queryParams.append(key, params[key].toString());
        }
      });
      return api.post(`/invoices/${id}/mydata/cancel?${queryParams}`);
    },
    getStatistics: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/invoices/summary/statistics?${queryString}`);
    },
    getOverdue: () => api.get('/invoices/overdue'),
    getCreditable: (clientId) => api.get(`/invoices/creditable/${clientId}`),
    createCredit: (referenceInvoiceId) => api.post('/invoices/create-credit', { reference_invoice_id: referenceInvoiceId }),
    checkItemsRepair: () => api.get('/invoices/items/check-repair'),
    fixItemsRepair: () => api.post('/invoices/items/fix-repair'),
    checkItemsQuantityRepair: () => api.get('/invoices/items/check-quantity-repair'),
    fixItemsQuantityRepair: () => api.post('/invoices/items/fix-quantity-repair'),
    checkItemsFullRepair: () => api.get('/invoices/items/check-full-repair'),
    fixItemsFullRepair: () => api.post('/invoices/items/fix-full-repair'),
    generatePDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  },

  // Scheduler endpoints
  scheduler: {
    getUpcomingServices: (daysAhead = 30) => {
      return api.get(`/scheduler/upcoming-services?days_ahead=${daysAhead}`);
    },
    earlyRenew: (serviceId) => api.post(`/scheduler/early-renew/${serviceId}`),
    sendExpirationWarnings: (serviceIds) =>
      api.post('/scheduler/send-expiration-warnings', { service_ids: serviceIds }),
  },

  // Service Renewals endpoints
  serviceRenewals: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/service-renewals/?${queryString}`);
    },
    getById: (id) => api.get(`/service-renewals/${id}`),
    cancel: (id) => api.post(`/service-renewals/${id}/cancel`),
    process: (id) => api.post(`/service-renewals/${id}/process`),
  },

  // Notifications endpoints
  notifications: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/notifications/?${queryString}`);
    },
    getById: (id) => api.get(`/notifications/${id}`),
    getUnreadCount: () => api.get('/notifications/unread-count'),
    markAsRead: (id) => api.put(`/notifications/${id}/read`),
    markAllAsRead: () => api.put('/notifications/read-all'),
    update: (id, data) => api.put(`/notifications/${id}`, data),
    delete: (id) => api.delete(`/notifications/${id}`),
    create: (data) => api.post('/notifications/', data),
  },

  // Settings endpoints
  settings: {
    getMyData: () => api.get('/settings/mydata'),
    updateMyData: (data) => api.put('/settings/mydata', data),
    getNotifications: () => api.get('/settings/notifications'),
    updateNotifications: (data) => api.put('/settings/notifications', data),
  },

  // Dashboard endpoints
  dashboard: {
    getRevenueActivity: (params = {}) => {
      const df = params.date_from && String(params.date_from).trim();
      const dt = params.date_to && String(params.date_to).trim();
      const clientStatus = params.client_status && params.client_status !== 'all'
        ? params.client_status
        : undefined;
      if (df && dt && df.length >= 10 && dt.length >= 10) {
        return api.post('/dashboard/revenue-activity/range', {
          date_from: df.slice(0, 10),
          date_to: dt.slice(0, 10),
          client_status: clientStatus || 'all',
        });
      }
      return api.get('/dashboard/revenue-activity', {
        params: { period: params.period || '6months', client_status: clientStatus || 'all' },
      });
    },
    getSubscriptionSummary: (params = {}) => api.get('/dashboard/subscription-summary', {
      params: { client_status: params.client_status || 'all' },
    }),
    getSummary: (period = '6months') => {
      return api.get(`/dashboard/summary?period=${period}`);
    },
  },

  // Quote Packages endpoints
  quotePackages: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/quote-packages/?${queryString}`);
    },
    getById: (id) => api.get(`/quote-packages/${id}`),
    create: (data) => api.post('/quote-packages/', data),
    update: (id, data) => api.put(`/quote-packages/${id}`, data),
    delete: (id) => api.delete(`/quote-packages/${id}`),
    duplicate: (id) => api.post(`/quote-packages/${id}/duplicate`),
    getSections: (id, params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/quote-packages/${id}/sections?${queryString}`);
    },
    createSection: (id, data) => api.post(`/quote-packages/${id}/sections`, data),
    updateSection: (packageId, sectionId, data) => api.put(`/quote-packages/${packageId}/sections/${sectionId}`, data),
    deleteSection: (packageId, sectionId) => api.delete(`/quote-packages/${packageId}/sections/${sectionId}`),
  },

  // Quotes endpoints
  quotes: {
    getAll: (params = {}) => {
      const queryString = buildQueryParams(params);
      return api.get(`/quotes/?${queryString}`);
    },
    getById: (id) => api.get(`/quotes/${id}`),
    create: (data) => api.post('/quotes/', data),
    update: (id, data) => api.put(`/quotes/${id}`, data),
    delete: (id) => api.delete(`/quotes/${id}`),
    addItem: (quoteId, data) => api.post(`/quotes/${quoteId}/items`, data),
    updateItem: (quoteId, itemId, data) => api.put(`/quotes/${quoteId}/items/${itemId}`, data),
    deleteItem: (quoteId, itemId) => api.delete(`/quotes/${quoteId}/items/${itemId}`),
    generatePDF: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
    getPreviewHTML: (id) => api.get(`/quotes/${id}/preview`),
    getShareLink: (id) => api.post(`/quotes/${id}/share-link`),
  },
};

// Export only APIClient (api instance is internal)
export default APIClient;
